<template>
  <div class="pb-2 max-h-full h-full">
    <div class="max-h-full h-full">
      <div class="flex min-h-full h-full overflow-hidden">
        <div class="flex-grow max-h-full overflow-auto py-3">
          <div v-if="responseType === 'response-text'" class="px-5">
            <div v-for="(textItem, index) in textItems" :key="textItem.id" class="flex items-center">
              <PhraseEditor v-model="textItem.text" :items="variables" class="mb-1 flex-grow" @update:modelValue="onTextPhraseChanged" />
              <IconButton class="ml-2 text-primary" :class="{ invisible: index + 1 === textItems.length && textItem.text === '' }" icon="delete" size="4" @click="onRemoveTextPhraseClicked(index)" />
            </div>
          </div>
          <div v-if="responseType === 'response-html'" class="px-5">
            <VariableSelect v-model="html.file" :items="variables" label="HTML (Text) Variable" :filter-data-type="['Text']" class="mb-1 flex-grow" @update:modelValue="emitPayload" />
          </div>
          <div v-if="responseType === 'response-image'" class="px-5">
            <VariableSelect v-model="image.file" multiple :items="variables" label="Image Variable" :filter-data-type="['Image']" class="mb-1 flex-grow" @update:modelValue="emitPayload" />
          </div>
          <div v-if="responseType === 'response-pdf'" class="px-5">
            <VariableSelect v-model="pdf.file" :items="variables" label="PDF Variable" :filter-data-type="['PDF']" class="mb-1 flex-grow" @update:modelValue="emitPayload" />
          </div>
          <div v-if="responseType === 'response-card'" class="px-5">
            <PhraseEditor v-model="card.title" :items="variables" :label="$t('title')" class="flex-grow" @update:modelValue="emitPayload" />
            <PhraseEditor v-model="card.text" :items="variables" :label="$t('evaluate.text')" class="mt-3 flex-grow" @update:modelValue="emitPayload" />
            <VariableSelect
              v-model="card.chart"
              multiple
              :label="$t('chart')"
              class="mt-3 flex-grow"
              selectBgColor="bg-white"
              :filter-data-type="['Chart']"
              :showCreate="false"
              @update:modelValue="emitPayload"
            />
            <VariableSelect
              v-model="card.table"
              :label="$t('table')"
              class="mt-3 flex-grow"
              selectBgColor="bg-white"
              :filter-data-type="['DataTable']"
              :showCreate="false"
              @update:modelValue="emitPayload"
            />
            <VariableSelect v-model="card.insights" multiple :label="$t('insights')" class="mt-3 flex-grow" selectBgColor="bg-white" :showCreate="false" @update:modelValue="emitPayload" />
            <VariableSelect
              v-model="card.date"
              multiple
              :label="$t('dates')"
              :filter-data-type="['Date']"
              class="mt-3 flex-grow"
              :showCreate="false"
              selectBgColor="bg-white"
              @update:modelValue="emitPayload"
            />
          </div>
          <div v-if="responseType === 'response-json'" class="px-5">
            <table class="w-full">
              <thead>
                <tr>
                  <th class="py-1 px-2 text-left border-b font-500 text-12 text-dark-text border-solid border-gray-200 pb-1">{{ $t('flow.property') }}</th>
                  <th class="py-1 px-2 text-left border-b font-500 text-12 text-dark-text border-solid border-gray-200 pb-1" width="160">{{ $t('flow.type') }}</th>
                  <th class="py-1 px-2 text-left border-b font-500 text-12 text-dark-text border-solid border-gray-200 pb-1" width="300">{{ $t('value') }}</th>
                  <th class="py-1 px-2 text-left border-b font-500 text-12 text-dark-text border-solid border-gray-200 pb-1" width="50"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(jsonItem, index) in jsonItems" :key="jsonItem.id">
                  <td class="px-1 py-1">
                    <FormInput v-model="jsonItem.key" class="min-w-100 w-full" @blur="onJsonKeyChanged" placeholder="Enter property name or use @ for root" />
                  </td>
                  <td class="px-1 py-1">
                    <FormSelect
                      v-model="jsonItem.type"
                      :options="[
                        { value: 'Variable', label: 'Variable' },
                        { value: 'Text', label: 'Text' },
                      ]"
                      class="w-40"
                      clearable
                      :searchable="false"
                      appendToBody
                      default-reducer
                      @option:selected="onJsonTypeSelected($event, jsonItem)"
                    />
                  </td>
                  <td class="px-1 py-1">
                    <template v-if="jsonItem.type === 'Variable'">
                      <VariableSelect v-model="jsonItem.value" @update:modelValue="onJsonVariableChanged" />
                    </template>
                    <template v-else-if="jsonItem.type === 'Text'">
                      <FormInput v-model="jsonItem.value" class="w-full" @update:modelValue="onJsonValueChanged" />
                    </template>
                  </td>
                  <td class="px-1 py-1">
                    <IconButton class="text-primary" :class="{ invisible: index + 1 === jsonItems.length && jsonItem.key === '' }" icon="delete" size="4" @click="onRemoveJsonItemClicked(index)" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="responseType === 'response-xml'" class="px-5">
            <VariableSelect v-model="xml.variable" :items="variables" label="Variable (Containing XML Text)" :filter-data-type="['Text']" class="mb-1 flex-grow" @update:modelValue="emitPayload" />
          </div>
          <div v-if="responseType === 'response-excel'" class="px-5">
            <VariableSelect v-model="excel.file" :items="variables" label="Excel Variable" :filter-data-type="['Excel']" class="mb-1 flex-grow" @update:modelValue="emitPayload" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import uuidv4 from '@/helpers/uuid';
import EvaluateItemsConfig from './EvaluateItemsConfig';
import debounce from '../../../helpers/debounce';

export default {
  name: 'ResponseConfig',
  props: {
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      textItems: [],
      card: structuredClone(EvaluateItemsConfig['response-card'].payload),
      jsonItems: [],
      pdf: structuredClone(EvaluateItemsConfig['response-pdf'].payload),
      image: structuredClone(EvaluateItemsConfig['response-image'].payload),
      html: structuredClone(EvaluateItemsConfig['response-html'].payload),
      text: '',
      xml: structuredClone(EvaluateItemsConfig['response-xml'].payload),
      excel: structuredClone(EvaluateItemsConfig['response-excel'].payload),
    };
  },
  created() {
    this.setInitialData(JSON.parse(JSON.stringify(this.item.payload)));

    this.checkTextFieldsForNew();
    this.checkJsonFieldsForNew();
  },
  computed: {
    ...mapState(['projectData']),
    ...mapGetters(['getProjectTemplates']),
    templates() {
      return Object.keys(this.getProjectTemplates()).map((key) => ({
        label: this.getProjectTemplates()[key].name,
        value: key,
      }));
    },
    responseType() {
      return this.item.type;
    },
    payload() {
      return {
        id: this.item.id,
        type: this.item.type,
        payload: this.responsePayload,
      };
    },
    responsePayload() {
      if (this.responseType === 'response-card') {
        return this.card;
      }
      if (this.responseType === 'response-json') {
        return structuredClone(this.jsonItems)
          .filter((s) => s.key)
          .map((s) => {
            delete s.id;
            return s;
          });
      }
      if (this.responseType === 'response-text') {
        return this.textItems.filter((s) => s.text).map((s) => s.text);
      }
      if (this.responseType === 'response-pdf') {
        return this.pdf;
      }
      if (this.responseType === 'response-image') {
        return this.image;
      }
      if (this.responseType === 'response-html') {
        return this.html;
      }
      if (this.responseType === 'response-xml') {
        return this.xml;
      }
      if (this.responseType === 'response-excel') {
        return this.excel;
      }
      return null;
    },
    projectId() {
      return this.$route.params.projectId;
    },
    variables() {
      let variables = [];
      if (this.projectId && Object.keys(this.projectData).length) {
        variables = Object.keys(this.projectData?.[this.projectId]?.variables || {}).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) || [];
      }
      variables = variables.map((variable) => ({
        id: uuidv4(),
        value: variable,
      }));
      return variables;
    },
  },
  methods: {
    // eslint-disable-next-line
    emitPayload: debounce(function () {
      this.$emit('update', structuredClone(this.payload));
    }, 300),
    setInitialData(responseData) {
      if (this.responseType === 'response-html') {
        this.html = { ...this.html, ...responseData };
      } else if (this.responseType === 'response-image') {
        this.image = { ...this.image, ...responseData };
      } else if (this.responseType === 'response-pdf') {
        this.pdf = { ...this.pdf, ...responseData };
      } else if (this.responseType === 'response-text') {
        this.textItems =
          responseData?.map((i) => ({
            id: uuidv4(),
            text: i,
          })) || [];
      } else if (this.responseType === 'response-card') {
        this.card = { ...this.card, ...responseData };
      } else if (this.responseType === 'response-json') {
        this.jsonItems = structuredClone(
          responseData?.map((i) => ({
            id: uuidv4(),
            ...i,
          })) || [],
        );
      } else if (this.responseType === 'response-xml') {
        this.xml = { ...this.xml, ...responseData };
      } else if (this.responseType === 'response-excel') {
        this.excel = { ...this.excel, ...responseData };
      }
    },
    onTextPhraseChanged() {
      this.checkTextFieldsForNew();
      this.emitPayload();
    },
    onJsonKeyChanged() {
      this.checkJsonFieldsForNew();
      this.emitPayload();
    },
    onJsonVariableChanged() {
      this.checkJsonFieldsForNew();
      this.emitPayload();
    },
    onJsonValueChanged() {
      this.checkJsonFieldsForNew();
      this.emitPayload();
    },
    onRemoveTextPhraseClicked(index) {
      if (index >= 0) {
        this.textItems.splice(index, 1);
        this.emitPayload();
      }
    },
    onRemoveJsonItemClicked(index) {
      if (index >= 0) {
        this.jsonItems.splice(index, 1);
        this.emitPayload();
      }
    },
    checkTextFieldsForNew() {
      if (this.textItems?.length > 0) {
        if (this.textItems[this.textItems.length - 1].text !== '') {
          this.addResponseTextItem();
        }
      } else {
        this.addResponseTextItem();
      }
    },
    checkJsonFieldsForNew() {
      if (this.jsonItems?.length > 0) {
        if (this.jsonItems[this.jsonItems.length - 1].key !== '') {
          this.addJsonItem();
        }
      } else {
        this.addJsonItem();
      }
    },
    addResponseTextItem() {
      this.textItems.push({ id: uuidv4(), text: '' });
    },
    addJsonItem() {
      this.jsonItems.push({ id: uuidv4(), key: '', type: '', value: '' });
    },
    onJsonTypeSelected(e, jsonItem) {
      jsonItem.type = e.value;
      jsonItem.value = null;
      this.checkJsonFieldsForNew();
      this.emitPayload();
    },
  },
};
</script>
